import { ActionBar, FAIconName } from '@equip.health/ui';

import {
  EquipScaleOrderStatus,
  OrderEquipScaleFrom,
} from '~/lib/constants/patients.constants';
import {
  getActiveOrder,
  getDeviceOrderDate,
  getDeviceStatus,
  getStatusHistoryDateString,
  isOrderCanceledOrFailed,
} from '~/lib/util/bodyTraceTracking.util';
import util from '~/lib/util/util';
import { useGetBodyTraceOrdersQuery } from '~/store/services/clinicalData';
import { PatientClinicalEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

interface EquipScaleActionBarProps {
  patientName: string;
  patientId: string;
  handleOrderScale: (from: OrderEquipScaleFrom) => void; //arg passed to check from device card or banner
}

const EquipScaleActionBar = ({
  patientId,
  patientName,
  handleOrderScale,
}: EquipScaleActionBarProps) => {
  const { data: bodyTraceOrderData, isLoading } =
    useGetBodyTraceOrdersQuery(patientId);
  const { trackEvent } = useAnalytics();

  const activeOrder = getActiveOrder(bodyTraceOrderData);
  const hasOrderData = !!activeOrder;

  const formattedDate = activeOrder && getStatusHistoryDateString(activeOrder);
  const deviceOrderDate =
    activeOrder && getDeviceOrderDate(activeOrder, formattedDate);

  const equipScaleOrdered =
    activeOrder?.status?.label === EquipScaleOrderStatus.REQUESTED;

  const equipScaleShipped =
    activeOrder?.status.label === EquipScaleOrderStatus.SHIPPED;

  const equipScaleDelivered =
    activeOrder?.status.label === EquipScaleOrderStatus.DELIVERED;
  const equipScaleConfirmed =
    activeOrder?.status.label === EquipScaleOrderStatus.CONFIRMED;

  const equipScaleActivated =
    activeOrder?.device.status.label === EquipScaleOrderStatus.ACTIVE;

  const activatedEquipScaleTimestamp = activeOrder?.device.dateActive;

  const shouldShowActionBarBasedOnActivatedTimestamp = (): boolean => {
    if (!activatedEquipScaleTimestamp) {
      return true;
    }
    const activationDate = new Date(activatedEquipScaleTimestamp);
    const currentDate = new Date();
    const differenceInTime = currentDate.getTime() - activationDate.getTime();
    const differenceInDays = differenceInTime / (1000 * 3600 * 24);

    return differenceInDays <= 7;
  };

  const getActionBarIcon = (): FAIconName | undefined => {
    if (!hasOrderData) return FAIconName.exclamationTriangleRegular;
    if (equipScaleDelivered && !equipScaleActivated)
      return FAIconName.clockRegular;
    if (equipScaleDelivered && equipScaleActivated)
      return FAIconName.checkRegular;
    return undefined;
  };

  const getActionBarText = (): string => {
    if (!hasOrderData) return `${patientName} does not have an Equip scale.`;
    if (equipScaleDelivered && !equipScaleActivated)
      return `${patientName}'s Equip scale was ${util.lowercaseFirstLetter(deviceOrderDate)} and needs to be activated.`;
    if (equipScaleDelivered && equipScaleActivated)
      return `${patientName}'s Equip scale was ${util.lowercaseFirstLetter(deviceOrderDate)}.`;
    return '';
  };

  const getActionBarButtonText = (): string => {
    if (!hasOrderData) return 'Order scale';
    if (equipScaleDelivered && !equipScaleActivated) return 'View instructions';
    return '';
  };

  const getActionBarButtonIcon = (): FAIconName | undefined => {
    if (!hasOrderData) return FAIconName.plusRegular;
    if (equipScaleDelivered && !equipScaleActivated)
      return FAIconName.externalLinkRegular;
    return undefined;
  };
  const handleButtonClick = () => {
    if (equipScaleDelivered && !equipScaleActivated) {
      window.open('https://www.equip.health/equip-scale', '_blank', 'noopener');
      trackEvent(PatientClinicalEvents.viewInstructionsClicked, {
        source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
      });
    } else {
      trackEvent(PatientClinicalEvents.bannerOrderScaleClicked, {
        source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
      });
      handleOrderScale(OrderEquipScaleFrom.BANNER);
    }
  };
  const deviceStatus = getDeviceStatus(activeOrder);
  const shouldShowActionBar =
    !equipScaleOrdered &&
    !equipScaleShipped &&
    !equipScaleConfirmed &&
    shouldShowActionBarBasedOnActivatedTimestamp() &&
    !isOrderCanceledOrFailed(deviceStatus) &&
    !isLoading;

  return (
    <>
      {shouldShowActionBar && (
        <ActionBar
          barIcon={getActionBarIcon()}
          buttonIcon={getActionBarButtonIcon()}
          buttonText={getActionBarButtonText()}
          id="Action-bar"
          onButtonClick={handleButtonClick}
          text={getActionBarText()}
          variant={equipScaleActivated ? 'primary' : 'warning'}
        />
      )}
    </>
  );
};

export default EquipScaleActionBar;
